import {
  AfterViewInit,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Signals } from 'deep-chat/dist/types/handler';
import { Connect } from 'deep-chat/dist/types/connect';
import { AiStore } from '../wh-ai.store';
import { WHLoginDataService } from '@workheld/workheld-shared-lib';
import { AuthService } from '@auth0/auth0-angular';
import 'deep-chat';
import { DeepChat } from 'deep-chat';
import { createWhAiChatConfig, AI_ENDPOINT } from '../config/wh-ai-chat.config';
import {
  buildAiHtmlResponse,
  getSourcesLabel,
  renderAiMarkdown,
} from '../config/wh-ai-response.utils';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSidenavModule } from '@angular/material/sidenav';
import { WHAiSessionHistoryComponent } from './wh-ai-session-history.component';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-wh-ai-chat',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    TranslateModule,
    MatSidenavModule,
    WHAiSessionHistoryComponent,
  ],
  styles: `
    :host {
      display: flex;
      width: 100%;
      height: 100%;
      overflow: hidden;
      position: relative;

      ::ng-deep {
        .mat-drawer-content {
          display: flex;
        }
      }
    }

    ::ng-deep {
      .mat-drawer-inner-container {
        overflow: hidden;
      }
    }

    .action-drawer-container {
      width: 100%;
    }

    .chat-loading-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(4px);
      z-index: 10;
    }
    .loading-dots {
      display: flex;
      gap: 6px;
      align-items: center;
    }
    .loading-dots span {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: var(--wh-ai-primary);
      animation: pulse 1.4s ease-in-out infinite;
    }
    .loading-dots span:nth-child(2) {
      animation-delay: 0.2s;
    }
    .loading-dots span:nth-child(3) {
      animation-delay: 0.4s;
    }
    @keyframes pulse {
      0%,
      100% {
        opacity: 0.4;
        transform: scale(0.8);
      }
      50% {
        opacity: 1;
        transform: scale(1);
      }
    }
  `,
  template: `
    <mat-drawer-container class="action-drawer-container" autosize>
      <mat-drawer
        style="width: 100%"
        position="end"
        [autoFocus]="false"
        #actionDrawer
        class="action-drawer p--16"
        mode="over"
      >
        <app-wh-ai-session-history />
      </mat-drawer>
      @if (aiStore.isLoadingChatHistory()) {
        <div class="chat-loading-overlay">
          <div class="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      }
      <deep-chat
        #deepChat
        style="height: calc(100vh - 68px);
          width: 100%;
          flex: 1;
          border: unset;"
        [inputAreaStyle]="config.inputAreaStyle"
        [textInput]="config.textInput"
        [messageStyles]="config.messageStyles"
        [submitButtonStyles]="config.submitButtonStyles"
        [history]="aiStore.activeSessionChatHistory()"
        [auxiliaryStyle]="config.auxiliaryStyle"
        [focusMode]="config.focusMode"
        [connect]="connectConfig"
      >
        <div
          class="wh-welcome-message"
          style="width: 90%;
          height: calc(100vh - 330px);
          display: flex;
          align-items: flex-end;
          justify-content: flex-start;"
        >
          <div>
            <div
              style="
                  color: #4a4a4a;
                  text-align: start;
                  margin-bottom: 8px;
                  font-size: 24px;
                  gap: 8px;
                "
            >
              <b>{{ 'app.ai.intro.title' | translate }}</b>
              @if (userName()) {
                <b
                  >, <span>{{ userName() }}?</span></b
                >
              }
            </div>
            @if (showSuggestions()) {
              <div class="wh-ai-suggestions-header-container">
                <p class="wh-ai-suggestions-header">
                  {{ 'app.ai.suggestions.label' | translate }}
                </p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-stars"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M7.657 6.247c.11-.33.576-.33.686 0l.645 1.937a2.89 2.89 0 0 0 1.829 1.828l1.936.645c.33.11.33.576 0 .686l-1.937.645a2.89 2.89 0 0 0-1.828 1.829l-.645 1.936a.361.361 0 0 1-.686 0l-.645-1.937a2.89 2.89 0 0 0-1.828-1.828l-1.937-.645a.361.361 0 0 1 0-.686l1.937-.645a2.89 2.89 0 0 0 1.828-1.828zM3.794 1.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387A1.73 1.73 0 0 0 4.593 5.69l-.387 1.162a.217.217 0 0 1-.412 0L3.407 5.69A1.73 1.73 0 0 0 2.31 4.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387A1.73 1.73 0 0 0 3.407 2.31zM10.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.16 1.16 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.16 1.16 0 0 0-.732-.732L9.1 2.137a.145.145 0 0 1 0-.274l.774-.258c.346-.115.617-.386.732-.732z"
                  />
                </svg>
              </div>
              <div class="wh-ai-suggestions">
                @for (
                  suggestion of aiStore.suggestions()?.slice(0, 5);
                  track suggestion;
                  let i = $index
                ) {
                  <button
                    type="button"
                    class="wh-ai-source-card"
                    data-cy="wh-ai-suggestion-card"
                    (mouseenter)="showSuggestionAsPlaceholder(suggestion.text)"
                    (mouseleave)="hideSuggestionPlaceholder()"
                    (click)="handleSuggestionClick(suggestion.text)"
                  >
                    <div class="wh-ai-source-card-header">
                      <span class="wh-ai-source-title">{{
                        suggestion.text | translate
                      }}</span>
                    </div>
                  </button>
                }
              </div>
            }
          </div>
        </div>
      </deep-chat>
    </mat-drawer-container>
  `,
})
export class WHAiChatComponent implements AfterViewInit, OnDestroy {
  @ViewChild('deepChat') deepChat: ElementRef<DeepChat>;
  @ViewChild('actionDrawer') actionDrawer: any;

  aiStore = inject(AiStore);
  authService = inject(AuthService);
  translateService = inject(TranslateService);
  loginDataService = inject(WHLoginDataService);
  activeUserDom = null;
  config = createWhAiChatConfig(this.translateService);
  connectConfig: Connect = {
    handler: this.chatHandler.bind(this),
    stream: {
      partialRender: true,
    },
  };
  readonly SESSION_TIMEOUT = 900_000;
  readonly STREAM_INACTIVITY_TIMEOUT = 90_000;
  private sourceClickHandler: ((event: Event) => void) | null = null;
  private loadingOriginByUrl = new Map<string, 'inline' | 'card'>();
  private activeAbortController: AbortController | null = null;
  private activeInactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  userName = computed(() => {
    return this.activeUserDom()?.firstName;
  });
  showSuggestions = computed(() => {
    return (
      this.aiStore.isAiActive() &&
      !this.aiStore.activeSessionId() &&
      this.aiStore.activeSessionChatHistory().length === 0 &&
      this.aiStore.suggestions().length > 0
    );
  });

  constructor() {
    this.activeUserDom = toSignal(this.loginDataService.activeUserDOM$);

    // Update config when language changes
    this.translateService.onLangChange.subscribe(() => {
      this.config = createWhAiChatConfig(this.translateService);
    });

    effect(() => {
      if (this.aiStore.activeSessionId() === null && this.deepChat) {
        this.deepChat.nativeElement.clearMessages();
      }
    });

    effect(() => {
      if (this.aiStore.isSessionHistoryActive()) {
        this.actionDrawer.open();
      } else if (this.actionDrawer) {
        this.actionDrawer.close();
      }
    });

    effect(() => {
      if (this.aiStore.isAiActive() && !this.aiStore.activeSessionId()) {
        this.aiStore.loadSuggestions();
      }
    });

    effect(() => {
      const activeSessionFromSessionStorage =
        sessionStorage.getItem('wh-ai-session');
      const parsedActiveSession = activeSessionFromSessionStorage
        ? JSON.parse(activeSessionFromSessionStorage)
        : null;

      if (
        this.aiStore.isAiActive() &&
        this.deepChat &&
        !this.aiStore.activeSessionId() &&
        (!parsedActiveSession ||
          Date.now() - parsedActiveSession.timestamp > this.SESSION_TIMEOUT)
      ) {
        this.deepChat.nativeElement.clearMessages();
      } else if (
        this.deepChat &&
        parsedActiveSession &&
        !this.aiStore.activeSessionId()
      ) {
        this.aiStore.loadChatHistory(parsedActiveSession.sessionId);
      }
    });

    effect(() => {
      const urls = this.aiStore.downloadingDocUrls();
      if (!this.deepChat) return;
      this.loadingOriginByUrl.forEach((_, url) => {
        if (!urls.has(url)) {
          this.loadingOriginByUrl.delete(url);
        }
      });

      const cards =
        this.deepChat.nativeElement.shadowRoot?.querySelectorAll<HTMLElement>(
          '.wh-ai-source-card[data-doc-url], .wh-ai-source-link[data-doc-url]',
        ) ?? [];
      cards.forEach((card) => {
        const docUrl = card.getAttribute('data-doc-url');
        const loadingOrigin = docUrl
          ? this.loadingOriginByUrl.get(docUrl)
          : undefined;
        const isInlineSource = card.classList.contains(
          'wh-ai-source-link--inline',
        );
        const isSourceCard = card.classList.contains('wh-ai-source-card');
        const shouldShowLoading =
          !!docUrl &&
          urls.has(docUrl) &&
          (!loadingOrigin ||
            (loadingOrigin === 'inline' && isInlineSource) ||
            (loadingOrigin === 'card' && isSourceCard));

        if (shouldShowLoading) {
          card.classList.add('is-loading');
        } else {
          card.classList.remove('is-loading');
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.sourceClickHandler = (event: Event) =>
      this.handleDocumentClick(event as MouseEvent);

    this.deepChat?.nativeElement?.addEventListener(
      'click',
      this.sourceClickHandler,
      true,
    );
  }

  ngOnDestroy(): void {
    if (this.sourceClickHandler) {
      this.deepChat?.nativeElement?.removeEventListener(
        'click',
        this.sourceClickHandler,
        true,
      );
    }

    this.loadingOriginByUrl.clear();

    this.destroyed = true;
    this.activeAbortController?.abort();
    if (this.activeInactivityTimer) {
      clearTimeout(this.activeInactivityTimer);
      this.activeInactivityTimer = null;
    }
  }

  private async chatHandler(body: any, signals: Signals): Promise<void> {
    const questionText = body?.messages?.[0]?.text ?? '';

    let sessionId = this.aiStore.activeSessionId();
    if (!sessionId) {
      try {
        sessionId = await firstValueFrom(this.aiStore.createNewSession());
      } catch {
        await signals.onResponse({ error: 'Failed to create session' });
        signals.onClose();
        return;
      }
    }

    // The component may have been destroyed while createNewSession() was
    // pending, before there was an AbortController for ngOnDestroy to abort.
    // Bail out here instead of starting a stream for a torn-down chat.
    if (this.destroyed) {
      return;
    }

    const abortController = new AbortController();
    this.activeAbortController = abortController;
    let userAborted = false;
    let timedOut = false;
    signals.stopClicked.listener = () => {
      userAborted = true;
      abortController.abort();
    };

    const resetInactivityTimer = () => {
      if (this.activeInactivityTimer) {
        clearTimeout(this.activeInactivityTimer);
      }
      this.activeInactivityTimer = setTimeout(() => {
        timedOut = true;
        abortController.abort();
      }, this.STREAM_INACTIVITY_TIMEOUT);
    };
    const clearInactivityTimer = () => {
      if (this.activeInactivityTimer) {
        clearTimeout(this.activeInactivityTimer);
        this.activeInactivityTimer = null;
      }
    };
    const finishRequest = () => {
      clearInactivityTimer();
      this.activeAbortController = null;
    };

    const handleAbortOrError = async (e: unknown) => {
      finishRequest();
      if (this.destroyed) {
        return;
      }
      if (userAborted) {
        signals.onClose();
        return;
      }
      Sentry.captureException(e);
      await signals.onResponse({
        error: timedOut
          ? this.getErrorLabel('timeout')
          : this.getErrorLabel('streamFailed'),
      });
      signals.onClose();
    };

    let accessToken = '';

    try {
      accessToken = await firstValueFrom(
        this.authService.getAccessTokenSilently(),
      );
    } catch {
      await signals.onResponse({ error: 'Failed to acquire access token' });
      signals.onClose();
      return;
    }

    let response: Response;
    resetInactivityTimer();
    try {
      response = await fetch(AI_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'X-Session-Id': sessionId,
        },
        body: JSON.stringify(questionText),
        signal: abortController.signal,
      });
    } catch (e: unknown) {
      await handleAbortOrError(e);
      return;
    }

    if (!response.ok) {
      finishRequest();
      const responseStatusText = response.statusText
        ? ` ${response.statusText}`
        : '';
      await signals.onResponse({
        error: `Request failed (${response.status}${responseStatusText})`,
      });
      signals.onClose();
      return;
    }

    if (!response.body) {
      finishRequest();
      await signals.onResponse({
        error: `Request failed (${response.status} empty response body)`,
      });
      signals.onClose();
      return;
    }

    signals.onOpen();

    const statusSteps: string[] = [];
    await signals.onResponse({
      html: buildStatusTimelineHtml(statusSteps),
      overwrite: true,
    });

    const extractingLabel =
      this.getStatusLabel('extracting') ?? 'Extracting sources';

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let accumulatedText = '';
    let buffer = '';
    let doneEvent: any = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        resetInactivityTimer();
        if (done) {
          buffer += decoder.decode();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop()!;

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;

          const jsonStr = trimmed.slice(5).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);
            if (event.type === 'status') {
              const statusLabel = this.getStatusLabel(event.phase);
              if (
                statusLabel &&
                statusLabel !== statusSteps[statusSteps.length - 1]
              ) {
                statusSteps.push(statusLabel);
                await signals.onResponse({
                  html: buildStatusTimelineHtml(statusSteps),
                  overwrite: true,
                });
              }
            } else if (event.type === 'delta' && event.content) {
              accumulatedText += event.content;
              await signals.onResponse({
                html: renderAiMarkdown(accumulatedText, extractingLabel),
                overwrite: true,
              });
            } else if (event.type === 'done') {
              doneEvent = event;
            }
          } catch {
            // skip malformed events
          }
        }
      }

      // Flush any remaining data in the buffer (handles missing trailing newline)
      const remaining = buffer.trim();
      if (remaining.startsWith('data:')) {
        const jsonStr = remaining.slice(5).trim();
        if (jsonStr) {
          try {
            const event = JSON.parse(jsonStr);
            if (event.type === 'delta' && event.content) {
              accumulatedText += event.content;
            } else if (event.type === 'done') {
              doneEvent = event;
            }
          } catch {
            // skip malformed events
          }
        }
      }
    } catch (e: unknown) {
      await handleAbortOrError(e);
      return;
    }

    finishRequest();

    if (this.destroyed) {
      return;
    }

    if (!doneEvent) {
      // The SSE body closed (clean EOF) without ever emitting a terminal
      // 'done' event - e.g. the backend crashed or the connection was cut
      // mid-response. Treat any partial output as a failure instead of
      // silently presenting it as a finished answer.
      Sentry.captureException(
        new Error('AI chat stream ended without a done event'),
      );
      await signals.onResponse({ error: this.getErrorLabel('streamFailed') });
      signals.onClose();
      return;
    }

    const locale = this.translateService.getCurrentLang();

    const html = buildAiHtmlResponse(
      {
        value: accumulatedText,
        annotations: doneEvent.annotations ?? [],
      },
      {
        runId: doneEvent.runId,
        questionText,
        createdAt: doneEvent.createdAt,
        locale,
        enableFeedback: true,
        sourcesLabel: getSourcesLabel(this.translateService),
      },
    );

    await signals.onResponse({ html, overwrite: true });
    signals.onClose();
  }

  handleSuggestionClick(suggestion: string): void {
    const translated = this.translateService.instant(suggestion);

    this.deepChat.nativeElement.submitUserMessage({ text: translated });
  }

  showSuggestionAsPlaceholder(suggestion: string): void {
    const translated = this.translateService.instant(suggestion);
    this.deepChat.nativeElement.setPlaceholderText(translated);
  }

  hideSuggestionPlaceholder(): void {
    this.deepChat.nativeElement.setPlaceholderText(
      this.translateService.instant('app.ai.askSomething.input.placeholder'),
    );
  }

  handleDocumentClick(event: MouseEvent): void {
    const feedbackButton = this.findFeedbackButton(event);
    if (feedbackButton) {
      this.handleFeedbackClick(event, feedbackButton);
      return;
    }

    const sourceElement = this.findSourceElement(event);
    if (!sourceElement) {
      return;
    }

    const url = sourceElement.getAttribute('data-doc-url');
    if (!url) {
      return;
    }

    if (this.aiStore.downloadingDocUrls().has(url)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.loadingOriginByUrl.set(url, this.getLoadingOrigin(sourceElement));
    this.aiStore.downloadDocumentFromUrl(url);
  }

  private getLoadingOrigin(sourceElement: HTMLElement): 'inline' | 'card' {
    if (sourceElement.classList.contains('wh-ai-source-link--inline')) {
      return 'inline';
    }

    return 'card';
  }

  private handleFeedbackClick(
    event: MouseEvent,
    feedbackButton: HTMLElement,
  ): void {
    const container = feedbackButton.closest('.wh-ai-feedback');
    if (!container) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const runId = container.getAttribute('data-run-id') ?? '';
    const question = container.getAttribute('data-question') ?? '';
    const answer = container.getAttribute('data-answer') ?? '';
    const feedbackValue =
      feedbackButton.getAttribute('data-feedback-value') ?? '';

    if (!runId || !question || !answer || !feedbackValue) {
      return;
    }

    const selected = container.getAttribute('data-feedback-selected') ?? '';
    if (selected === feedbackValue) {
      return;
    }

    const sessionId = this.aiStore.activeSessionId();
    if (!sessionId) {
      return;
    }

    const positiveFeedback = feedbackValue === 'positive';
    container.setAttribute('data-feedback-selected', feedbackValue);
    this.updateFeedbackButtons(container, feedbackValue);

    this.aiStore.sendFeedback({
      runId,
      sessionId,
      positiveFeedback,
      question,
      answer,
    });
  }

  private updateFeedbackButtons(
    container: Element,
    feedbackValue: string,
  ): void {
    const buttons = container.querySelectorAll<HTMLElement>(
      '.wh-ai-feedback-button',
    );
    buttons.forEach((button) => {
      const value = button.getAttribute('data-feedback-value');
      if (value === feedbackValue) {
        button.classList.add('is-active');
        button.classList.remove('is-hidden');
      } else {
        button.classList.remove('is-active');
        button.classList.add('is-hidden');
      }
    });
  }

  private findSourceElement(event: MouseEvent): HTMLElement | null {
    const target = event.target as HTMLElement | null;
    if (target?.closest) {
      const directMatch = target.closest(
        '.wh-ai-source-card[data-doc-url], .wh-ai-source-link[data-doc-url]',
      );
      if (directMatch) {
        return directMatch as HTMLElement;
      }
    }

    const path = event.composedPath?.() ?? [];
    for (const node of path) {
      if (node instanceof HTMLElement) {
        if (
          node.matches(
            '.wh-ai-source-card[data-doc-url], .wh-ai-source-link[data-doc-url]',
          )
        ) {
          return node;
        }
      }
    }

    return null;
  }

  private findFeedbackButton(event: MouseEvent): HTMLElement | null {
    const target = event.target as HTMLElement | null;
    if (target?.closest) {
      const directMatch = target.closest('.wh-ai-feedback-button');
      if (directMatch) {
        return directMatch as HTMLElement;
      }
    }

    const path = event.composedPath?.() ?? [];
    for (const node of path) {
      if (node instanceof HTMLElement) {
        if (node.classList.contains('wh-ai-feedback-button')) {
          return node;
        }
      }
    }

    return null;
  }

  private getStatusLabel(phase: string): string | null {
    const phaseKey = `app.ai.status.phase.${phase}`;
    const phaseTranslated = this.translateService.instant(phaseKey);
    if (phaseTranslated !== phaseKey) {
      return phaseTranslated;
    }

    return STATUS_FALLBACK_LABELS[phase] ?? null;
  }

  private getErrorLabel(kind: 'streamFailed' | 'timeout'): string {
    const errorKey = `app.ai.error.${kind}.label`;
    const errorTranslated = this.translateService.instant(errorKey);
    if (errorTranslated !== errorKey) {
      return errorTranslated;
    }

    return ERROR_FALLBACK_LABELS[kind];
  }
}

const STATUS_FALLBACK_LABELS: Record<string, string> = {
  started: 'Thinking',
  prepared: 'Preparing',
  discovering: 'Discovering tools',
  discovered: 'Tools ready',
  executing: 'Searching',
  completed: 'Processing results',
  extracting: 'Extracting sources',
};

const ERROR_FALLBACK_LABELS: Record<'streamFailed' | 'timeout', string> = {
  streamFailed: 'Something went wrong. Please try again.',
  timeout: 'The request took too long. Please try again.',
};

function buildStatusTimelineHtml(steps: string[]): string {
  const items = steps
    .map((label, i) => {
      const isLast = i === steps.length - 1;
      const stepClass = isLast
        ? 'wh-ai-timeline-step is-active'
        : 'wh-ai-timeline-step is-done';
      return `<div class="${stepClass}">
        <div class="wh-ai-timeline-dot"></div>
        <span class="wh-ai-timeline-label">${escapeHtml(label)}</span>
      </div>`;
    })
    .join('');

  return `<div class="wh-ai-timeline">${items}</div>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
